import { 
  Mail, 
  Users, 
  TrendingUp, 
  Search, 
  MessageSquare, 
  Zap,
  Brain,
  Shield,
  Target,
  BarChart3,
  Megaphone,
  Globe,
  Lock,
  Settings,
  Star,
  CheckCircle,
  DollarSign,
  Clock,
  Eye,
  UserCheck,
  Activity,
  Rocket,
  Database,
  Network
} from 'lucide-react';

export const marketingSalesSuiteFeatures = {
  keyBenefits: [
    {
      title: 'Complete Marketing Automation',
      description: 'Streamline all marketing activities with AI-powered automation, from lead generation to customer retention, increasing efficiency by 400% and reducing manual work.',
      icon: Zap,
      metrics: '400% efficiency increase, 80% time savings, 95% automation rate',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Unified Sales Intelligence',
      description: 'Comprehensive CRM with predictive analytics, sales forecasting, and performance tracking to increase sales conversion rates by 65% and improve deal velocity.',
      icon: Brain,
      metrics: '65% higher conversion, 3x faster deal closure, 95% forecast accuracy',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Advanced Customer Analytics',
      description: 'Deep customer insights with behavioral tracking, lifetime value optimization, and churn prediction to improve customer retention by 85% and increase revenue per customer.',
      icon: BarChart3,
      metrics: '85% better retention, 120% revenue increase, 360° customer view',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Multi-Channel Integration',
      description: 'Seamlessly manage email, social media, SMS, WhatsApp, and web campaigns from one platform, ensuring consistent messaging and 300% better engagement.',
      icon: Globe,
      metrics: '300% engagement boost, 15+ channels, 100% message consistency',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Enterprise Security & Compliance',
      description: 'Bank-level security with GDPR compliance, advanced encryption, and audit trails to protect customer data and maintain regulatory compliance.',
      icon: Shield,
      metrics: '99.9% uptime, GDPR compliant, SOC 2 certified',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Real-Time Performance Optimization',
      description: 'Live campaign monitoring, automatic optimization, and instant insights to maximize ROI and reduce customer acquisition costs by 45%.',
      icon: Target,
      metrics: '45% lower acquisition cost, Real-time optimization, 250% ROI improvement',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Omnichannel Orchestration',
      description: 'Coordinate email, ads, social, SMS, web personalization and sales touchpoints in one canvas for a seamless customer journey.',
      icon: Settings,
      metrics: '1 canvas, 15+ channels, zero silos',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'Account-Based Marketing (ABM)',
      description: 'Target and personalize experiences for high-value accounts with intent, firmographic data, and sales collaboration.',
      icon: Users,
      metrics: '3x deal size, 2x win rate, precise targeting',
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Revenue Attribution & MMM',
      description: 'Multi-touch attribution and media mix modeling to understand channel contribution and optimize spend.',
      icon: BarChart3,
      metrics: '95% attribution coverage, optimized CAC/LTV',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Real-Time Personalization',
      description: 'On-site and in-email personalization powered by AI segments and behavioral signals.',
      icon: Star,
      metrics: '20–40% lift in conversion, dynamic content',
      color: 'from-amber-500 to-orange-600'
    },
    {
      title: 'Lead Management & Scoring',
      description: 'Unified capture, progressive profiling, and AI scoring to prioritize sales-ready leads.',
      icon: Target,
      metrics: '2x MQL→SQL rate, faster handoffs',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Sales–Marketing Alignment',
      description: 'Shared dashboards, SLAs, and playbooks to align teams from first touch to closed-won.',
      icon: CheckCircle,
      metrics: '50% faster cycle, fewer leaks',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Compliance & Consent Management',
      description: 'Granular consent, preference centres, and audit trails across channels and regions.',
      icon: Shield,
      metrics: 'GDPR/PECR ready, full auditability',
      color: 'from-slate-500 to-gray-600'
    },
    {
      title: 'Speed to Market',
      description: 'Prebuilt blueprints and AI-assisted setup to launch campaigns and pipelines in days, not months.',
      icon: Zap,
      metrics: 'Launch in days, not months',
      color: 'from-green-500 to-emerald-600'
    }
  ],
  
  advancedCapabilities: [
    {
      title: 'AI-Powered Email Builder',
      description: 'Advanced drag-and-drop email builder with AI content generation, personalization, and deliverability optimization.',
      icon: Mail,
      metrics: '500+ templates, AI content generation, Advanced personalization, A/B testing',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Comprehensive CRM System',
      description: 'Full-featured CRM with contact management, deal tracking, and sales pipeline automation.',
      icon: Users,
      metrics: '360-degree profiles, Pipeline automation, E-signatures, Revenue forecasting',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Social Media Management Hub',
      description: 'Complete social media management with scheduling, monitoring, and analytics across all major platforms.',
      icon: MessageSquare,
      metrics: 'Multi-platform posting, Social listening, Influencer management, ROI tracking',
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'SEO Optimization Suite',
      description: 'Advanced SEO tools with keyword tracking, content optimization, and technical SEO auditing.',
      icon: Search,
      metrics: 'Keyword tracking, Technical auditing, AI content optimization, Link building',
      color: 'from-orange-500 to-red-600'
    },
    {
      title: 'Predictive Analytics Engine',
      description: 'Machine learning-powered analytics for customer behavior prediction and marketing optimization.',
      icon: TrendingUp,
      metrics: '95% prediction accuracy, Churn prediction, LTV optimization, Lead scoring',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Marketing Automation Workflows',
      description: 'Visual workflow builder for creating complex, multi-channel marketing automation campaigns.',
      icon: Settings,
      metrics: 'Visual workflow builder, Multi-channel orchestration, Event triggers, A/B testing',
      color: 'from-gray-500 to-slate-600'
    },
    {
      title: 'Lead Generation & Nurturing',
      description: 'Comprehensive lead generation tools with advanced nurturing capabilities and conversion optimization.',
      icon: Target,
      metrics: 'Multi-channel capture, Progressive profiling, Lead scoring, Conversion optimization',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Communication Center',
      description: 'Unified inbox for all customer communications across email, SMS, WhatsApp, and social media.',
      icon: MessageSquare,
      metrics: 'Unified inbox, WhatsApp Business, SMS marketing, Video messaging',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'Sales Intelligence Platform',
      description: 'Advanced sales tools with conversation intelligence, coaching, and performance analytics.',
      icon: Brain,
      metrics: 'Call recording, Conversation analysis, Sales coaching, Revenue attribution',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Customer Success Management',
      description: 'Comprehensive customer success tools for retention, upselling, and satisfaction tracking.',
      icon: UserCheck,
      metrics: 'Health score monitoring, Automated workflows, Upsell opportunities, Loyalty programs',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Advanced Reporting & Analytics',
      description: 'Comprehensive analytics dashboard with custom reporting and real-time performance monitoring.',
      icon: BarChart3,
      metrics: 'Real-time dashboard, 200+ metrics, Custom reports, ROI calculation',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Enterprise Integration Suite',
      description: 'Seamless integration with 200+ third-party platforms and custom API development.',
      icon: Globe,
      metrics: '200+ integrations, Custom API, SSO integration, White-label options',
      color: 'from-rose-500 to-pink-600'
    },
    {
      title: 'Ad Platform Integrations',
      description: 'Native connections to Google, Meta, LinkedIn, TikTok with audiences, conversions, and offline events sync.',
      icon: Megaphone,
      metrics: '4+ ad networks, first‑party conversions, audience sync',
      color: 'from-blue-600 to-cyan-600'
    },
    {
      title: 'Customer Data Platform (CDP)',
      description: 'Unify identities and events into golden profiles with consent and privacy controls.',
      icon: Shield,
      metrics: 'Deterministic identity, consent ledger, profile API',
      color: 'from-slate-600 to-gray-700'
    },
    {
      title: 'Journey Builder',
      description: 'Drag‑and‑drop orchestration for lifecycle, onboarding, upsell, and win‑back programs.',
      icon: Settings,
      metrics: 'Reusable nodes, testing, versioning',
      color: 'from-teal-500 to-emerald-600'
    },
    {
      title: 'Web Personalization & Experiments',
      description: 'Dynamic content, banners, and experiments powered by segments and intent.',
      icon: Star,
      metrics: 'Audience rules, no‑code widgets, A/B/n tests',
      color: 'from-amber-500 to-orange-600'
    },
    {
      title: 'Conversational Marketing',
      description: 'AI chat, playbooks, and meeting routing to capture and qualify demand instantly.',
      icon: MessageSquare,
      metrics: '24/7 capture, instant routing, higher conversion',
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Webinar & Events',
      description: 'Plan, promote, and run webinars and events with integrated registration and follow‑ups.',
      icon: Clock,
      metrics: 'Registration → nurture, attendance analytics',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Partner & Affiliate Management',
      description: 'Track partner referrals, payouts, and co‑marketing attribution.',
      icon: Users,
      metrics: 'Transparent attribution, automated payouts',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'API & Webhooks',
      description: 'Robust APIs and real‑time webhooks for custom apps, data sync, and server-side events.',
      icon: Globe,
      metrics: 'Real‑time delivery, signed webhooks, SDKs',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Data Warehouse Export',
      description: 'Stream events and aggregates to BigQuery/Snowflake for advanced modeling.',
      icon: BarChart3,
      metrics: 'Daily/hourly exports, schema stable, GDPR safe',
      color: 'from-violet-500 to-purple-600'
    }
  ],
  
  securityPerformance: [
    {
      title: 'Compliance Excellence Framework',
      description: 'Comprehensive compliance management with automated auditing, policy enforcement, and regulatory reporting across global standards including GDPR, SOC 2, ISO 27001, and industry-specific requirements.',
      icon: Target,
      metrics: 'SOC 2 Type II, ISO 27001, GDPR Article 30, 99% audit success',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Security Performance Analytics',
      description: 'Advanced security metrics dashboard with real-time threat monitoring, vulnerability assessments, and performance benchmarking against industry standards with automated remediation workflows.',
      icon: Activity,
      metrics: 'Real-time monitoring, 99.9% threat detection, <2min response time',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Scalable Security Infrastructure',
      description: 'Enterprise-grade security architecture with auto-scaling threat protection, distributed security controls, and global compliance monitoring that grows with your business needs.',
      icon: Rocket,
      metrics: '10M+ events/second, 99.99% uptime, Global coverage',
      color: 'from-purple-500 to-violet-600'
    },
    {
      title: 'Advanced Firewall & DDoS Protection',
      description: 'Multi-layer security defense with AI-powered threat detection, advanced persistent threat (APT) protection, and distributed denial-of-service (DDoS) mitigation capabilities.',
      icon: Shield,
      metrics: '500+ Gbps DDoS protection, 99.99% attack mitigation, AI threat detection',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Multi-Factor Authentication Suite',
      description: 'Comprehensive identity verification with biometric authentication, hardware tokens, smart cards, and adaptive authentication policies based on risk assessment and user behavior.',
      icon: Eye,
      metrics: '99.9% authentication success, 5+ MFA methods, Zero trust architecture',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Security Information & Event Management',
      description: 'Advanced SIEM capabilities with machine learning-powered threat detection, automated incident response, and comprehensive security event correlation and analysis.',
      icon: Database,
      metrics: 'ML-powered detection, <1min incident response, 360° visibility',
      color: 'from-orange-500 to-red-600'
    },
    {
      title: 'Data Loss Prevention & Backup',
      description: 'Comprehensive data protection with automated backup systems, encrypted storage, point-in-time recovery, and advanced data loss prevention policies and monitoring.',
      icon: Lock,
      metrics: '99.999% data durability, 15-min RPO, Automated encryption',
      color: 'from-gray-500 to-slate-600'
    },
    {
      title: 'Network Segmentation & Monitoring',
      description: 'Advanced network security with micro-segmentation, zero-trust networking, continuous network monitoring, and automated threat isolation and containment protocols.',
      icon: Network,
      metrics: 'Zero-trust networking, Real-time monitoring, Automated isolation',
      color: 'from-teal-500 to-emerald-600'
    },
    {
      title: 'Continuous Security Assessment',
      description: 'Ongoing security evaluation with automated penetration testing, vulnerability scanning, security posture assessment, and compliance validation with detailed reporting.',
      icon: CheckCircle,
      metrics: 'Daily vulnerability scans, Automated pen testing, 100% compliance tracking',
      color: 'from-indigo-500 to-blue-600'
    }
  ]
};